/**
 * One refresh at a time, across every tab on this origin.
 *
 * Web Locks is the real implementation. The browser grants the lock to one
 * context at a time and releases it when that context goes away, which is the
 * "the winning tab died mid-refresh" case handled by the platform rather than
 * by a timeout we would have to guess at. Every browser the back office runs in
 * has it, with one exception: it is not exposed in an insecure context, and a
 * driver's phone pointed at a LAN address over plain http is one.
 *
 * So there is a second implementation, for that case only: a claim in
 * localStorage carrying a timestamp the holder keeps beating, which a waiter
 * takes over once the beating stops. That is best effort rather than mutual
 * exclusion — two tabs writing in the same instant can both believe they hold
 * it — and it is the honest trade for a path where the browser offers nothing
 * better. It narrows the window from "always open" to "a few milliseconds".
 */

const REFRESH_LOCK = 'dryclean.staff.refresh';
const CLAIM_KEY = 'dryclean.staff.refreshClaim';

/** A claim this old without a beat belongs to a tab that is gone. */
const CLAIM_TTL_MS = 5_000;
/** Well inside the TTL, so a slow refresh keeps its own claim alive. */
const HEARTBEAT_MS = 1_500;
const POLL_MS = 120;
/** localStorage is last-write-wins; a beat of daylight before re-reading settles who wrote last. */
const SETTLE_MS = 60;
/**
 * A waiter gives up waiting and proceeds. The HTTP client's own deadline is 20s,
 * so a holder that is alive at all has finished by now, and a tab spinning
 * forever on a session it could have recovered is the worse failure.
 */
const MAX_WAIT_MS = 25_000;

function delay(ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Typed as always present, absent in an insecure context. Hence the check. */
function lockManager(): LockManager | null {
  return 'locks' in navigator ? navigator.locks : null;
}

/* ------------------------------------------------- the localStorage fallback */

interface Claim {
  holder: string;
  at: number;
}

function readClaim(): Claim | null {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(CLAIM_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as { holder?: unknown; at?: unknown };
    if (typeof candidate.holder !== 'string' || typeof candidate.at !== 'number') return null;
    return { holder: candidate.holder, at: candidate.at };
  } catch {
    // Garbage, or half a write. No claim at all is the safe reading: a value
    // nobody can parse must not be a lock nobody can ever take over.
    return null;
  }
}

function writeClaim(claim: Claim): boolean {
  try {
    window.localStorage.setItem(CLAIM_KEY, JSON.stringify(claim));
    return true;
  } catch {
    return false;
  }
}

function releaseClaim(holder: string): void {
  const claim = readClaim();
  // Not ours to drop: a waiter that took it over while we were slow keeps it.
  if (claim !== null && claim.holder !== holder) return;
  try {
    window.localStorage.removeItem(CLAIM_KEY);
  } catch {
    // It ages out on its own.
  }
}

async function tryClaim(holder: string): Promise<boolean> {
  const existing = readClaim();
  const now = Date.now();
  if (existing !== null && existing.holder !== holder) {
    const age = now - existing.at;
    // A stamp in the future is a clock that moved, not a holder that is alive.
    if (age >= 0 && age < CLAIM_TTL_MS) return false;
  }

  // No storage to coordinate through, so this tab is alone whether it likes it
  // or not — and the refresh still has to happen.
  if (!writeClaim({ holder, at: now })) return true;

  await delay(SETTLE_MS);
  const settled = readClaim();
  return settled !== null && settled.holder === holder;
}

async function acquireClaim(holder: string): Promise<void> {
  const deadline = Date.now() + MAX_WAIT_MS;
  for (;;) {
    if (await tryClaim(holder)) return;
    if (Date.now() >= deadline) return;
    await delay(POLL_MS);
  }
}

function startHeartbeat(holder: string): () => void {
  const timer = window.setInterval(() => {
    const claim = readClaim();
    // Taken over already: let the new holder have it rather than leaving two
    // tabs both convinced they are holding the same claim.
    if (claim !== null && claim.holder !== holder) return;
    writeClaim({ holder, at: Date.now() });
  }, HEARTBEAT_MS);

  return () => {
    window.clearInterval(timer);
  };
}

async function runWithClaim<T>(run: () => Promise<T>): Promise<T> {
  const holder = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  await acquireClaim(holder);
  const stopHeartbeat = startHeartbeat(holder);
  try {
    return await run();
  } finally {
    stopHeartbeat();
    releaseClaim(holder);
  }
}

/* ---------------------------------------------------------------- the lock */

/**
 * Waits for whoever holds the refresh lock, then runs `run` holding it.
 *
 * A loser waits for the winner to finish rather than racing it, which is the
 * whole point: it then reads what the winner wrote instead of spending the
 * token the winner has already rotated away.
 */
export async function withRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const manager = lockManager();
  if (manager === null) return runWithClaim(run);

  /*
   * request() resolves with whatever the callback returned, but the DOM lib
   * types that as `any`. Keeping hold of the callback's own promise is how the
   * result stays typed without an assertion.
   */
  const held: { promise?: Promise<T> } = {};
  await manager.request(REFRESH_LOCK, () => {
    held.promise = run();
    return held.promise;
  });

  if (held.promise === undefined) {
    throw new Error('The refresh lock was granted without running');
  }
  return held.promise;
}
